import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { TableRow } from '@ui/components';
import { cn } from '@ui/lib/utils';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useAppTableContext } from '../../lib/context/use-app-table-context';
import { useVirtualTable } from '../../lib/hooks/use-virtual-table';
import { AppTableRef } from '../../types/table-props';
import { AppTableCell } from '../app-table-cell';
import { AppTableDragHeaderFilter, AppTableHeader } from '../app-table-header';
import { AppTableToolbar } from '../app-table-toolbar';
import { TableCard } from '../table-card';

const TableComponent = <TData,>(
  _: never,
  ref: ForwardedRef<AppTableRef<TData>>,
) => {
  const {
    className,
    rowClassName,
    calculatedRowClassName,
    calculatedRowStyle,
    data,
    rowSelection,
    isDraggable,
    columns,
    setRowSelection,
    getRowId,
    onClickRow,
    onDoubleClickRow,
    absoluteColumnWidth,
    enableColumnFilter,
    rowHeight = 38,
    customSelectColumn,
    columnPinning,
    isTemplate,
    defaultSorting,
  } = useAppTableContext<TData>();

  const {
    table,
    rows,
    tableContainerRef,
    rowVirtualizer,
    columnOrder,
    setColumnOrder,
  } = useVirtualTable({
    data,
    columns,
    rowSelection,
    setRowSelection,
    getRowId,
    rowHeight,
    customSelectColumn,
    columnPinning,
    isTemplate,
    defaultSorting,
  });

  const totalColumnsWidth = useMemo(() => {
    return table
      .getFlatHeaders()
      .reduce((acc, header) => acc + header.getSize(), 0);
  }, [
    table.getState().columnSizingInfo,
    table.getState().columnSizing,
    columns,
  ]);

  const getColumnWidth = (size: number) => {
    if (absoluteColumnWidth) {
      return size;
    }
    return `${(size / totalColumnsWidth) * 100}%`;
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        return arrayMove(columnOrder, oldIndex, newIndex);
      });
    }
  }, []);

  useImperativeHandle(ref, () => ({
    getTable: () => table,
  }));

  return (
    <TableCard className={className}>
      <AppTableToolbar table={table} />
      <DndContext
        autoScroll={false}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        sensors={sensors}
        onDragEnd={handleDragEnd}
      >
        <div
          ref={tableContainerRef}
          className={cn('relative h-full overflow-auto')}
        >
          <table
            className="grid w-full"
            style={{
              display: 'grid',
              width: '100%',
            }}
          >
            <thead className="sticky top-0 z-10 grid w-full border-t">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="flex w-full bg-white"
                >
                  <SortableContext
                    disabled={!isDraggable}
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <AppTableHeader
                        key={header.id}
                        className="flex"
                        header={header}
                        style={{
                          width: getColumnWidth(header.getSize()),
                        }}
                      />
                    ))}
                  </SortableContext>
                </TableRow>
              ))}
              {enableColumnFilter &&
                table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="flex w-full bg-white"
                  >
                    <SortableContext
                      disabled={!isDraggable}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => (
                        <AppTableDragHeaderFilter
                          key={header.id}
                          header={header}
                          style={{
                            width: getColumnWidth(header.getSize()),
                          }}
                        />
                      ))}
                    </SortableContext>
                  </TableRow>
                ))}
            </thead>
            <tbody
              className="relative w-full"
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index];
                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      'absolute flex w-full',
                      rowClassName,
                      calculatedRowClassName?.(row.original, row),
                      onClickRow || onDoubleClickRow
                        ? 'cursor-pointer'
                        : 'cursor-default',
                    )}
                    data-index={virtualRow.index}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      width: '100%',
                      height: rowHeight,
                      ...calculatedRowStyle?.(row.original, row),
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <AppTableCell
                          key={cell.id}
                          cell={cell}
                          className="flex truncate text-nowrap"
                          style={{
                            width: getColumnWidth(cell.column.getSize()),
                          }}
                        />
                      );
                    })}
                  </TableRow>
                );
              })}
            </tbody>
          </table>
        </div>
      </DndContext>
    </TableCard>
  );
};

export const VirtualTable = forwardRef(TableComponent) as <TData>(
  props: never & {
    ref?: ForwardedRef<AppTableRef<TData>>;
  },
) => ReturnType<typeof TableComponent>;
